import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Input, Tag, Select, Spin, message, Typography } from 'antd';
import { SearchOutlined, CloudSyncOutlined } from '@ant-design/icons';
import { productService, vendorService } from '../services/api';

const { Option } = Select;
const { Title } = Typography;

const Products = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scrapingId, setScrapingId] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchVendors();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAll();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const data = await vendorService.getAll();
      setVendors(data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleScrape = async (id) => {
    try {
      setScrapingId(id);
      await productService.scrapeProductData(id);
      message.success('Product data scraped successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error scraping product data:', error);
      message.error('Failed to scrape product data');
    } finally {
      setScrapingId(null);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleVendorChange = (value) => {
    setSelectedVendor(value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter products based on search term, category, and vendor
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm ? 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) : 
      true;
    
    const matchesCategory = selectedCategory ? 
      product.category === selectedCategory : 
      true;
    
    const matchesVendor = selectedVendor ? 
      (product.vendors && product.vendors.some(v => v.id === parseInt(selectedVendor))) : 
      true;
    
    return matchesSearch && matchesCategory && matchesVendor;
  });

  // Extract unique categories from products for the filter dropdown
  const categories = [...new Set(products.map(product => product.category))].filter(Boolean);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const colorMap = {
          'Software': 'blue',
          'Hardware': 'green',
          'Furniture': 'orange',
          'Office Supplies': 'purple',
          'Electronics': 'cyan',
        };
        return category ? <Tag color={colorMap[category] || 'default'}>{category}</Tag> : 'N/A';
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => price ? `$${price.toLocaleString()}` : 'N/A',
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => {
        return record.vendors && record.vendors.length > 0 
          ? record.vendors.map(v => v.name).join(', ')
          : 'N/A';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<CloudSyncOutlined />} 
            size="small"
            onClick={() => handleScrape(record.id)}
            loading={scrapingId === record.id}
          >
            Scrape Data
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Products</Title>
      
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <Input 
            placeholder="Search products" 
            prefix={<SearchOutlined />} 
            style={{ width: 300 }}
            value={searchTerm}
            onChange={handleSearch}
          />
          
          <Select 
            style={{ width: 200 }}
            placeholder="Filter by Category"
            allowClear
            onChange={handleCategoryChange}
            value={selectedCategory}
          >
            {categories.map(category => (
              <Option key={category} value={category}>{category}</Option>
            ))}
          </Select>

          <Select 
            style={{ width: 200 }}
            placeholder="Filter by Vendor"
            allowClear
            onChange={handleVendorChange}
            value={selectedVendor}
          >
            {vendors.map(vendor => (
              <Option key={vendor.id} value={vendor.id}>{vendor.name}</Option>
            ))}
          </Select>
        </div>
        
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredProducts}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default Products;